import React, { useContext, useEffect, useRef, useState } from "react";
import localizationStrings from "../../statics/localization";
import {
  getRecordedList,
  getWillBeRecordedList,
  getUnavailableRecordList,
  checkNpvrQuota,
  getSeriesDetail
} from "../../api/npvr/npvr";
import { useHistory, useLocation } from "react-router-dom";
import ReturnIcon from "../../assets/icons/svg/ReturnIcon";
import { CircularProgressbarWithChildren, buildStyles } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import UserRecordItem from "../userRecordItem/UserRecordItem";
import { SessionContext } from "../../context/SessionContext";
import ModalPortal from "../modalPortal";
import ProgramDetail from "../programDetail";
import { getIsFromPip, setIsFromPip } from "../../statics/config";
import mainLogo from "../../assets/logo/atlas-logo.png";
import "./UserRecords.scss";
import useMountedRef from "../../utils/useMountedRef";
import { quotaDisplay } from "../../utils/recording";
import { flowCopy } from "../../utils/flowCopy";

const UserRecords = () => {
  const [records, setRecords] = useState([]);
  const [npvrQuota, setNpvrQuota] = useState("");
  const [npvrRemaining, setNpvrRemaining] = useState("");
  const [percentage, setPercentage] = useState(0);
  const [isLimitless, setIsLimitless] = useState(false);
  const [recordCount, setRecordCount] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState(1);
  const [showSeriesEpisode, setShowSeriesEpisode] = useState(false);
  const [selectedSerieRecord, setSelectedSerieRecord] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const {
    userFrameConfig,
    userInfo,
    setSelectedCutvProgram,
    setPlayUrl,
    playUrl,
    hasLoggedIn
  } = useContext(SessionContext);
  const allRecords = useRef([]);
  const mounted = useMountedRef();
  const listVersion = useRef(0);
  const quotaVersion = useRef(0);
  const [listStatus, setListStatus] = useState("loading");
  const [quotaError, setQuotaError] = useState(false);
  const history = useHistory();
  const location = useLocation();

  let isFromPip = getIsFromPip();

  const toArray = (value) => (Array.isArray(value) ? value : []);

  useEffect(() => {
    setShowSeriesEpisode(false);
    getRecords();
    return () => { listVersion.current++; };
  }, [selectedFilter]);

  const loadRecords = async (loader) => {
    const version = ++listVersion.current;
    setListStatus("loading");
    setRecords([]);
    try {
      const response = await loader();
      if (!mounted.current || version !== listVersion.current) return;
      if (response?.status !== 200 || !Array.isArray(response.data)) throw new Error("Records unavailable");
      allRecords.current = response.data;
      setRecords(response.data);
      setListStatus("ready");
    } catch (e) {
      if (mounted.current && version === listVersion.current) setListStatus("error");
    }
  };
  const getRecords = () => loadRecords(selectedFilter === 1 ? getRecordedList : selectedFilter === 2 ? getWillBeRecordedList : getUnavailableRecordList);
  const getSeriesRecordings = record => loadRecords(() => getSeriesDetail(
    record.epgProgramId, record.seriesId, selectedFilter === 1 ? "recorded" : "recording"
  ));

  const renderedItems = records?.map((item) => {
    return (
      <UserRecordItem
        key={item.id || item.epgProgramId || item.title}
        item={item}
        setDeletedRecordCode={(code) => {
          getRecords();
          getNpvrQuota();
        }}
        clickRecord={(record, action = "default") => {
          if (selectedFilter !== 1) return;

          if (action === "play") {
            const vodId =
              record?.vodUId ||
              record?.VodUId ||
              record?.vodUid ||
              record?.VodUid ||
              record?.contentId ||
              record?.ContentId ||
              record?.contentUId ||
              record?.ContentUId;

            if (vodId) {
              history.push(`/dashboard/voddetail/${vodId}`);
              return;
            }

            // Fallback: open record detail directly instead of doing list-expand logic.
            setSelectedRecord(record);
            setShowModal(true);
            return;
          }

          if (
            !!record?.seriesId &&
            (selectedFilter === 1 || selectedFilter === 2) &&
            !showSeriesEpisode
          ) {
            getSeriesRecordings(record);
            setShowSeriesEpisode(true);
            setSelectedSerieRecord(record);
          } else {
            setSelectedRecord(record);
            setShowModal(true);
          }
        }}
        isEpisode={selectedFilter === 1 ? showSeriesEpisode : true}
        selectedFilter={selectedFilter}
        isPlayable={selectedFilter === 1}
        setPopupOpened={() => { }}
      />
    );
  });

  const getNpvrQuota = async () => {
    const version = ++quotaVersion.current;
    setQuotaError(false);
    try {
      const response = await checkNpvrQuota();
      if (!mounted.current || version !== quotaVersion.current) return;
      if (response.status !== 200) throw new Error("Quota unavailable");
      const quota = quotaDisplay(response.data);
      setIsLimitless(quota.isLimitless);
      setNpvrQuota(quota.used);
      setNpvrRemaining(quota.remaining === null ? null : Math.floor(quota.remaining));
      setPercentage(quota.percentage);
      setRecordCount(Number.isFinite(response.data.recordLimit) && Number.isFinite(response.data.usedRecords)
        ? { used: response.data.usedRecords, limit: response.data.recordLimit } : null);
    } catch (e) {
      if (mounted.current && version === quotaVersion.current) setQuotaError(true);
    }
  };

  //life cycle methods
  useEffect(() => {
    getNpvrQuota();
  }, []);

  useEffect(() => {
    if (isFromPip) {
      setSelectedRecord(isFromPip);
      setShowModal(true);
      setIsFromPip(null);
    }
  }, [isFromPip]);

  return (
    <div className="user-records-container">
      <div className="settings-container">
        <div
          className="settings-icon"
          style={{
            position: "fixed",
            left: `${(33 / 1920) * 100}vw`,
            top: `calc(${(72 / 1920) * 100}vw + ${(49 / 1920) * 100}vw)`,
            zIndex: 5,
            margin: 0,
            gap: "10px"
          }}
          onClick={() => {
            if (showSeriesEpisode) {
              setShowSeriesEpisode(false);
              getRecords();
            } else {
              history.push(`/dashboard/settings`);
            }
          }}
        >
          <ReturnIcon />
          <div className="icon-settings">{localizationStrings.back}</div>
        </div>
        <div className="pc-header">
          <img className="pc-logo" src={mainLogo} alt="Atlas" />
          <div className="pc-title" style={{ marginBottom: "30px" }}>{localizationStrings.myRecords}</div>
        </div>
        <div>
          <div style={{ padding: "0 5vw" }}>

            <div
              className="edit-record-wrapper"
              style={{ display: showSeriesEpisode || quotaError ? "none" : "" }}
            >
              <div className="record">
                <div className="serie-record">
                  <label>
                    <span>{localizationStrings.totalRecordedContent + " " + npvrQuota + " " + localizationStrings.minutes}</span>
                  </label>
                  <div className="quota" style={{ textAlign: 'center' }}>{isLimitless ? "∞" : localizationStrings.formatString(localizationStrings.total, {
                    percentage: parseInt(percentage)
                  })}</div>
                </div>
                <div className="icon" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <div
                    style={{
                      width: '3.25vw',
                      height: '3.25vw',
                      borderRadius: '50%',
                      backgroundColor: '#F15A23',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      fontWeight: 'normal'
                    }}
                  >
                    <span id="recording-percentage">
                      {isLimitless ? "∞" : `${parseInt(percentage)}%`}
                    </span>
                  </div>
                </div>
                <div className="record-serie">
                  <label>
                    <span>
                      {(isLimitless ? "∞" : npvrRemaining) + " " + localizationStrings.minutesOfRemainingRecordingTime}
                    </span>
                  </label>
                  <div>{recordCount && localizationStrings.formatString(localizationStrings.recordingCount, recordCount)}</div>
                </div>
              </div>
            </div>

            <div
              className="filter-option "
              style={{ display: showSeriesEpisode ? "none" : "" }}
            >
              <label
                className={`yellow ${selectedFilter === 1 ? "selected" : ""}`}
                onClick={() => setSelectedFilter(1)}
                style={{ fontSize: '1.2em' }}
              >
                {localizationStrings.recordSaved}
              </label>

              <label
                className={`blue ${selectedFilter === 2 ? "selected" : ""}`}
                onClick={() => setSelectedFilter(2)}
                style={{ fontSize: '1.2em' }}
              >
                {localizationStrings.toBeRegistered}
              </label>
              <label className={selectedFilter === 3 ? "selected" : ""} onClick={() => setSelectedFilter(3)}>
                {localizationStrings.unavailableRecords}
              </label>
            </div>
            {quotaError && <div role="alert">{flowCopy().loadError} <button onClick={getNpvrQuota}>{flowCopy().retry}</button></div>}
            {listStatus === "loading" && <div role="status">{flowCopy().loading}</div>}
            {listStatus === "error" && <div role="alert">{flowCopy().loadError} <button onClick={() => showSeriesEpisode ? getSeriesRecordings(selectedSerieRecord) : getRecords()}>{flowCopy().retry}</button></div>}
            {selectedFilter === 1 ? (
              <div className="record-items-wrapper">
                {renderedItems}
                {listStatus === "ready" && records.length === 0 ? (
                  <div className="not-found" style={{
                    textAlign: 'center',
                    opacity: 0.7,
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    marginTop: '2rem'
                  }}>{localizationStrings.noRecords}</div>
                ) : null}
              </div>
            ) : null}
            {selectedFilter !== 1 ? (
              <div className="record-items-wrapper">
                {renderedItems}
                {listStatus === "ready" && records.length === 0 ? (
                  <div className="not-found" style={{
                    textAlign: 'center',
                    opacity: 0.7,
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    marginTop: '2rem'
                  }}>{localizationStrings.noRecords}</div>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>

        {showModal ? (
          <ModalPortal viewClass="vod-modal">
            {/* <ProgramDetail
            epgProgramId={selectedRecord?.epgProgramId}
            seriesHeadId={selectedRecord?.seriesheadid}
            closeModal={(val) => {
              setShowModal(false);
            }}
            isRecord={true}
            recordItem={selectedRecord}
            userFrameConfig={userFrameConfig}
            userInfo={userInfo}
            location={location}
            history={history}
            directlyPlay={true}
          /> */}
            <ProgramDetail
              prevueCode={selectedRecord?.epgProgramId}
              closeModal={() => setShowModal(false)}
              fromLive={true}
              setSelectedCutvProgram={setSelectedCutvProgram}
              userFrameConfig={userFrameConfig}
              userInfo={userInfo}
              location={location}
              history={history}
              programData={selectedRecord}
              isRecord={true}
              setPlayUrl={setPlayUrl}
              playUrl={playUrl}
              hasLoggedIn={hasLoggedIn}
            />
          </ModalPortal>
        ) : null}
      </div>
    </div>
  );
};

export default UserRecords;
